import React from 'react';
import { useIntl } from 'react-intl';

import { queryClient } from 'src/lib/react-query';
import ConfirmationModal from 'src/registry/components/blocks/ConfirmationModal';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { toast } from 'src/hooks/useToast';
import { ICON_MAP } from 'src/utils/constants';
import { useOnError } from 'src/utils/errors';
import { useDeleteSubscriptionItemQuantityEvent } from 'src/features/subscriptionItemQuantityEvents/api/useDeleteSubscriptionItemQuantityEvent';
import { LIST_SUBSCRIPTION_ITEM_QUANTITY_EVENTS_RESOURCE } from 'src/features/subscriptionItemQuantityEvents/api/useListSubscriptionItemQuantityEvents';
import { subItemQuantityEventMessages } from 'src/features/subscriptionItemQuantityEvents/utils/messages';
import { SubscriptionItemQuantityEvent } from 'src/features/subscriptionItemQuantityEvents/utils/types';

interface DeleteSubscriptionItemQuantityEventFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedQuantityEvent: SubscriptionItemQuantityEvent;
    children?: React.ReactNode;
}

function DeleteSubscriptionItemQuantityEventFormModal<TData>({
    selectedQuantityEvent,
    children,
}: DeleteSubscriptionItemQuantityEventFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();
    const onError = useOnError();

    const { mutate: deleteQuantityEvent } = useDeleteSubscriptionItemQuantityEvent({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_SUBSCRIPTION_ITEM_QUANTITY_EVENTS_RESOURCE] });
            setIsModalOpen(false);
            toast({
                title: formatMessage(subItemQuantityEventMessages.deleteQuantityEventSuccess),
                description: formatMessage(subItemQuantityEventMessages.deleteQuantityEventSuccessDescription),
            });
        },
        onError: (error) => {
            onError(error, () => null);
        },
    });

    return (
        <ConfirmationModal
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            dialogDetails={{
                title: { ...subItemQuantityEventMessages.deleteQuantityEvent },
                subTitle: { ...subItemQuantityEventMessages.deleteQuantityEventSubtitle },
                icon: ICON_MAP.DELETE,
                confirmationMessage: { ...subItemQuantityEventMessages.confirmDeleteQuantityEvent },
            }}
            callbackButton={{
                variant: 'destructiveLight',
                buttonTitle: subItemQuantityEventMessages.deleteQuantityEvent,
                callbackFn: () => {
                    deleteQuantityEvent({ quantityEventId: selectedQuantityEvent.id });
                    setIsModalOpen(false);
                },
            }}
            children={children}
        />
    );
}

export default DeleteSubscriptionItemQuantityEventFormModal;
